import './AllProperties.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import Modal from 'react-modal';
import Slider from 'react-slick';
import { useNavigate } from 'react-router-dom';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import React, { useState, useEffect, useRef } from 'react';
import screenfull from 'screenfull';

const SkeletonStyles = () => (
  <style>{`
    .skeleton {
      position: relative;
      overflow: hidden;
      background: #e9eef5;
    }
    .skeleton::after {
      content: '';
      position: absolute;
      top: 0; left: -150px; bottom: 0;
      width: 150px;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
      animation: shimmer 1.2s infinite;
    }
    @keyframes shimmer {
      0% { transform: translateX(0); }
      100% { transform: translateX(300%); }
    }
  `}</style>
);

const SkeletonCard = () => (
  <div className="property-card">
    <div className="property-image skeleton" style={{
      position: 'relative', width: '100%', height: 0, paddingBottom: '75%', borderRadius: '6px'
    }} />
    <div className="property-content" style={{ paddingTop: '10px' }}>
      <div className="skeleton" style={{ height: '18px', width: '70%', borderRadius: '4px', marginBottom: '8px' }} />
      <div className="skeleton" style={{ height: '14px', width: '50%', borderRadius: '4px', marginBottom: '12px' }} />
      <div className="skeleton" style={{ height: '16px', width: '40%', borderRadius: '4px' }} />
    </div>
  </div>
);

const AllProperties = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [cities, setCities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage] = useState(12);
  const [filters, setFilters] = useState({
    keyword: '',
    location: '',
    property_type: '',
    price_min: '',
    price_max: '',
    is_premium: false
  });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const base_url = 'https://api.homehive.co.zw/api';
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const token = localStorage.getItem('token');
  const [activeNav, setActiveNav] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const [subscriptionPlans] = useState([
    {
      id: 1,
      name: 'Monthly Access',
      price: 0.01,
      duration: '/month',
      features: [
        'Contact unlimited landlords',
        'Chat with property owners',
        'Schedule viewings',
        
      ]
    }
  ]);

  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState(null);

  const [paymentFormData, setPaymentFormData] = useState({
    amount: subscriptionPlans[0].price,
    payment_method: '',
    phone: ''
  });

  const checkExistingConversation = async (propertyId, landlordId) => {
    try {
      const response = await axios.get(
        `${base_url}/conversations/`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const existingConversation = response.data.find(conv => 
        conv.property?.id === propertyId && 
        conv.participants.some(p => p.id === landlordId)
      );

      return existingConversation;
    } catch (error) {
      console.error('Error checking conversations:', error);
      return null;
    }
  };

  const handleContactClick = async (action, propertyId) => {
    if (!currentUser) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'Please log in to contact the landlord',
        icon: 'warning',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }

    // Check if user is subscribed
    if (!currentUser?.is_subscribed) {
      Swal.fire({
        title: 'Subscription Required',
        text: 'You need to subscribe to contact landlords. Would you like to subscribe now?',
        icon: 'info',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Not Now',
        confirmButtonText: 'Subscribe'
      }).then((result) => {
        if (result.isConfirmed) {
          setShowSubscriptionModal(true);
        }
      });
      return;
    }

    // Handle different contact actions
    switch(action) {
      case 'chat':
        if (!selectedProperty) return;

        setChatLoading(true);
        try {
          const existingConversation = await checkExistingConversation(
            propertyId,
            selectedProperty.owner.id
          );

          if (existingConversation) {
            // If conversation exists, navigate to existing chat
            navigate(`/chat/${existingConversation.id}`);
          } else {
            // If no conversation exists, create new one
            const response = await axios.post(
              `${base_url}/conversations/`,
              {
                property_id: propertyId,
                user_id: selectedProperty.owner.id
              },
              {
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            
            // Navigate to new chat
            navigate(`/chat/${response.data.id}`);
          }
        } catch (error) {
          console.error('Error handling chat:', error);
          Swal.fire({
            title: 'Error',
            text: 'Failed to start conversation. Please try again.',
            icon: 'error',
            confirmButtonColor: '#1a237e'
          });
        } finally {
          setChatLoading(false);
        }
        break;

      case 'call':
        window.location.href = `tel:${selectedProperty.owner.phone}`;
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${selectedProperty.owner.phone}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:${selectedProperty.owner.email}`;
        break;
      default:
        break;
    }
  };

  const handleSubscriptionClick = () => {
    if (!currentUser) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'Please log in to access premium features',
        icon: 'warning',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    setShowSubscriptionModal(true);
  };

  const handleSubscribe = async (planId) => {
    if (!currentUser) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'Please log in to subscribe',
        icon: 'warning',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }

    if (!paymentMethod) {
      Swal.fire({
        title: 'Select Payment Method',
        text: 'Please select a payment method to continue',
        icon: 'warning',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    try {
      setIsProcessingPayment(true);

      // Validate amount
      if (!paymentFormData.amount || paymentFormData.amount <= 0) {
        throw new Error('Invalid payment amount');
      }

      // For EcoCash payments, get phone number
      let phoneNumber = '';
      if (paymentMethod === 'ecocash') {
        const result = await Swal.fire({
          title: 'Enter EcoCash Phone Number',
          input: 'tel',
          inputLabel: 'Phone Number',
          inputPlaceholder: 'Enter your EcoCash number',
          showCancelButton: true,
          inputValidator: (value) => {
            if (!value) {
              return 'Please enter your phone number';
            }
            // Basic Zimbabwe phone number validation
            const phoneRegex = /^(?:\+263|0)7[7-8][0-9]{7}$/;
            if (!phoneRegex.test(value)) {
              return 'Please enter a valid Zimbabwe phone number';
            }
          },
          confirmButtonColor: '#1a237e',
          cancelButtonColor: '#64748b'
        });

        if (result.dismiss) {
          setIsProcessingPayment(false);
          return;
        }

        phoneNumber = result.value;
        if (!phoneNumber) {
          setIsProcessingPayment(false);
          return;
        }
      }

      // Prepare payment data
      const paymentData = {
        amount: parseFloat(paymentFormData.amount.toString()),
        payment_method: paymentMethod
      };

      // Add phone number for EcoCash payments
      if (paymentMethod === 'ecocash') {
        paymentData.phone = phoneNumber;
      }

      // Initiate payment
      const response = await axios.post(
        `${base_url}/payment/`,
        paymentData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        const { reference, pollUrl, instructions } = response.data;

        if (paymentMethod === 'ecocash') {
          // Show loading dialog instead of confirmation button
          Swal.fire({
            title: 'Processing EcoCash Payment',
            text: 'Please check your phone for the payment prompt and enter your PIN to confirm.',
            icon: 'info',
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Cancel Payment',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
        } else if (paymentMethod === 'visa') {
          // Redirect to payment gateway
          window.location.href = pollUrl;
          return;
        }

        // Start polling for payment status
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              `${base_url}/payment/`,
              {
                params: {
                  reference: reference,
                  pollUrl: pollUrl
                },
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );

            const { status, message } = statusResponse.data;

            if (status === 'paid') {
              clearInterval(pollInterval);
              setPaymentCheckInterval(null);
              setIsProcessingPayment(false);
              setShowSubscriptionModal(false);

              Swal.fire({
                title: 'Subscription Successful!',
                text: message || 'You can now contact landlords and access all features',
                icon: 'success',
                confirmButtonColor: '#1a237e'
              }).then(() => {
                // Refresh page to update subscription status
                window.location.reload();
              });
            } else if (status === 'cancelled' || status === 'failed') {
              clearInterval(pollInterval);
              setPaymentCheckInterval(null);
              setIsProcessingPayment(false);

              Swal.fire({
                title: 'Payment Failed',
                text: message || 'Payment was cancelled or failed. Please try again.',
                icon: 'error',
                confirmButtonColor: '#1a237e'
              });
            }
            // Continue polling for 'pending' status
          } catch (error) {
            console.error('Error checking payment status:', error);
            clearInterval(pollInterval);
            setPaymentCheckInterval(null);
            setIsProcessingPayment(false);

            Swal.fire({
              title: 'Error',
              text: error.response?.data?.detail || 'Failed to check payment status',
              icon: 'error',
              confirmButtonColor: '#1a237e'
            });
          }
        }, 5000);

        setPaymentCheckInterval(pollInterval);
      } else {
        throw new Error(response.data.detail || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessingPayment(false);
      
      Swal.fire({
        title: 'Payment Error',
        text: error.response?.data?.detail || error.message || 'Payment initiation failed',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  // Update payment method selection in subscription modal
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setPaymentFormData(prev => ({
      ...prev,
      payment_method: method,
      phone: method === 'ecocash' ? prev.phone : ''
    }));
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
      }
    };
  }, [paymentCheckInterval]);

  useEffect(() => {
    fetchProperties();
  }, []);

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in kilometers
    return distance;
  };

  // Get user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Unable to get your location. Showing all properties.');
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser. Showing all properties.');
    }
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${base_url}/properties`);
      const apiData = response?.data;
      const results = Array.isArray(apiData?.results)
        ? apiData.results
        : Array.isArray(apiData?.data)
          ? apiData.data
          : Array.isArray(apiData)
            ? apiData
            : [];
      console.log("properties", results);
      setProperties(results);
      setFilteredProperties(results);
      setNextUrl(apiData?.next || null);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching properties:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load properties',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      setIsLoading(false);
    }
  };

  const loadMoreProperties = async () => {
    if (!nextUrl) return;
    try {
      setIsFetchingMore(true);
      const response = await axios.get(nextUrl);
      const apiData = response?.data;
      const results = Array.isArray(apiData?.results)
        ? apiData.results
        : Array.isArray(apiData?.data)
          ? apiData.data
          : Array.isArray(apiData)
            ? apiData
            : [];
      const merged = Array.isArray(properties) ? [...properties, ...results] : results;
      setProperties(merged);
      setNextUrl(apiData?.next || null);
    } catch (error) {
      console.error('Error loading more properties:', error);
    } finally {
      setIsFetchingMore(false);
    }
  };


  const filterProperties = () => {
    let filtered = Array.isArray(properties) ? [...properties] : [];

    // Apply keyword filter
    if (filters.keyword) {
      filtered = filtered.filter(property => 
        property.description.toLowerCase().includes(filters.keyword.toLowerCase())
      );
    }

    // Apply location filter
    if (filters.location) {
      filtered = filtered.filter(property => 
        property.location.toString() === filters.location
      );
    }

    // Apply property type filter
    if (filters.property_type) {
      filtered = filtered.filter(property => 
        property.property_type === filters.property_type
      );
    }

    // Apply price filters
    if (filters.price_min) {
      filtered = filtered.filter(property => 
        property.price >= parseFloat(filters.price_min)
      );
    }

    if (filters.price_max) {
      filtered = filtered.filter(property => 
        property.price <= parseFloat(filters.price_max)
      );
    }

    // Filter by distance if user location is available
    if (userLocation) {
      filtered = filtered.filter(property => {
        if (!property.latitude || !property.longitude) return false;
        
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          property.latitude,
          property.longitude
        );
        
        return distance <= 10; // Show properties within 10km
      });
    }

    setFilteredProperties(Array.isArray(filtered) ? filtered : []);
    setCurrentPage(1);
  };

  // Update filters when user location changes
  useEffect(() => {
    filterProperties();
  }, [userLocation, filters, properties]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality here using filters state
    console.log('Search filters:', filters);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Pagination logic (show all for infinite scroll)
  const safeFiltered = Array.isArray(filteredProperties) ? filteredProperties : [];
  const currentProperties = safeFiltered;
  const totalPages = Math.ceil(safeFiltered.length / propertiesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const CustomPrevArrow = (props) => (
    <button {...props} className="slick-arrow slick-prev">
      <i className="fas fa-chevron-left"></i>
    </button>
  );

  const CustomNextArrow = (props) => (
    <button {...props} className="slick-arrow slick-next">
      <i className="fas fa-chevron-right"></i>
    </button>
  );

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    lazyLoad: 'ondemand',
    fade: true,
    cssEase: 'cubic-bezier(0.7, 0, 0.3, 1)'
  };


  const PropertyCard = ({ property }) => {
    return (
      <div className="property-card">
        <div className="property-image" style={{
          position: 'relative',
          width: '100%',
          height: '0',
          paddingBottom: '75%', // Default aspect ratio for larger screens
          overflow: 'hidden'
        }}>
          <div
            className={`availability-badge ${property.availability_status === 'available' ? 'available' : 'occupied'}`}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 2,
              background: property.availability_status === 'available' ? '#43a047' : '#e53935',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '0.8em',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
            }}
          >
            {property.availability_status === 'available' ? 'Available' : 'Occupied'}
          </div>
          <img 
            src={base_url + property.images[0]?.image || '/images/homehive.jpg'} 
            alt={property.location} 
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div className="property-type text-light">
            {property.property_type_display}
          </div>
          
        </div>
        <div className="property-content">
          <h3 className="property-title">{property.property_type_display} in {property.location}</h3>
          <div className="property-location">
            <i className="fas fa-map-marker-alt"></i>
            <span>{property.location}</span>
            <span className="price">${property.monthly_rental || property.price_per_night}</span>
            <span className="period">
              {property.property_type === 'lodge' ? '/night' : '/month'}
            </span>
          </div>

          <div className="property-price">
            
          </div>
        </div>
      </div>
    );
  };

  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && !isLoading && !isFetchingMore && nextUrl) {
        loadMoreProperties();
      }
    }, { root: null, rootMargin: '0px', threshold: 0.1 });
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [isLoading, isFetchingMore, nextUrl]);

  if (isLoading) {
    return (
      <div className="properties-section">
        <SkeletonStyles />
        <div className="innerHeading">
          <div className="container">
            <h1>Find Your Dream Home</h1>
          </div>
        </div>
        <div className="">
          <div className="container">
            <div className="row listing_wrap">
              <div className="col-lg-12">
                <div className="properties-grid">
                  {Array.from({ length: 12 }).map((_, idx) => (
                    <SkeletonCard key={idx} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="properties-section">
      {locationError && (
        <div style={{
          background: 'rgba(255, 244, 229, 1)',
          color: '#664d03',
          padding: '12px',
          borderRadius: '4px',
          margin: '10px 0',
          textAlign: 'center'
        }}>
          {locationError}
        </div>
      )}
      <div className="innerHeading">
        <div className="container">
          <h1>Find Your Dream Home</h1>
        </div>
      </div>

      <div className="">
        <div className="container">
          <div className="row listing_wrap">
            {/* Desktop Filter Section - Hidden on Mobile */}
            <div className="col-lg-12 mb-4 desktop-filters">
              <div className="filter-container card p-3">
                <div className="row">
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by description"
                      name="keyword"
                      value={filters.keyword}
                      onChange={handleFilterChange}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <select
                      className="form-control"
                      name="property_type"
                      value={filters.property_type}
                      onChange={handleFilterChange}
                    >
                      <option value="">Property Type</option>
                      <option value="apartment">Apartment</option>
                      <option value="full_house">Full House</option>
                      <option value="room">Room</option>
                      <option value="lodge">Lodge</option>
                      <option value="boarding_house">Boarding House</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <div className="price-range-inputs d-flex gap-2">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Min Price"
                        name="price_min"
                        value={filters.price_min}
                        onChange={handleFilterChange}
                        min="0"
                      />
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Max Price"
                        name="price_max"
                        value={filters.price_max}
                        onChange={handleFilterChange}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <button 
                      style={{backgroundColor: 'navy', color: '#fff'}}
                      className="btn w-100"
                      onClick={handleSubscriptionClick}
                      
                    >
                      <i className="fas fa-crown me-2"></i>
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Properties Grid */}
            <div className="col-lg-12">
              <div className="properties-grid">
                {currentProperties.map((property) => (
                  <div 
                    key={property.id} 
                    onClick={() => window.location.href = `/properties/${property.id}`}
                    className={property.is_premium ? 'premium-property' : ''}
                  >
                    <PropertyCard property={property} />
                  </div>
                ))}
              </div>
              {isFetchingMore && (
                <>
                  <SkeletonStyles />
                  <div className="properties-grid" style={{ marginTop: '16px' }}>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <SkeletonCard key={`more-${idx}`} />
                    ))}
                  </div>
                </>
              )}
              <div ref={loadMoreRef} style={{ height: '1px' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filter Menu - Slides down from top */}
      <div className={`mobile-filter-menu ${showMobileFilters ? 'show' : ''}`}>
        <div className="mobile-filters">
          <div className="filter-header">
            <h3>Filters</h3>
            <button 
              className="close-filters"
              onClick={() => setShowMobileFilters(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Search by description"
            name="keyword"
            value={filters.keyword}
            onChange={handleFilterChange}
          />
          <select
            className="form-control mb-2"
            name="property_type"
            value={filters.property_type}
            onChange={handleFilterChange}
          >
            <option value="">Property Type</option>
            <option value="apartment">Apartment</option>
            <option value="full_house">Full House</option>
            <option value="room">Room</option>
            <option value="lodge">Lodge</option>
            <option value="boarding_house">Boarding House</option>
          </select>
          <div className="price-range">
            <input
              type="number"
              className="form-control mb-2"
              placeholder="Min Price"
              name="price_min"
              value={filters.price_min}
              onChange={handleFilterChange}
            />
            <input
              type="number"
              className="form-control"
              placeholder="Max Price"
              name="price_max"
              value={filters.price_max}
              onChange={handleFilterChange}
            />
          </div>
        </div>
      </div>

      {/* Filter Toggle Button - Fixed position */}
      <button 
        className="filter-toggle-btn"
        onClick={() => setShowMobileFilters(!showMobileFilters)}
      >
        <i className={`fas fa-${showMobileFilters ? 'times' : 'filter'}`}></i>
      </button>


      
      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <div className="nav-items">
          <div 
            className={`nav-item ${activeNav === 'all' ? 'active' : ''}`}
            onClick={() => {
              setActiveNav('all');
              setFilters({ ...filters, property_type: '' });
            }}
          >
            <i className="fas fa-home"></i>
            <span>All</span>
          </div>
          <div 
            className={`nav-item ${activeNav === 'apartment' ? 'active' : ''}`}
            onClick={() => {
              setActiveNav('apartment');
              setFilters({ ...filters, property_type: 'apartment' });
            }}
          >
            <i className="fas fa-building"></i>
            <span>Apartments</span>
          </div>
          <div 
            className={`nav-item ${activeNav === 'house' ? 'active' : ''}`}
            onClick={() => {
              setActiveNav('house');
              setFilters({ ...filters, property_type: 'house' });
            }}
          >
            <i className="fas fa-home"></i>
            <span>Houses</span>
          </div>
          <div 
            className={`nav-item ${activeNav === 'lodge' ? 'active' : ''}`}
            onClick={() => {
              setActiveNav('lodge');
              setFilters({ ...filters, property_type: 'lodge' });
            }}
          >
            <i className="fas fa-hotel"></i>
            <span>Lodges</span>
          </div>
          <div 
            className={`nav-item ${activeNav === 'boarding' ? 'active' : ''}`}
            onClick={() => {
              setActiveNav('boarding');
              setFilters({ ...filters, property_type: 'boarding' });
            }}
          >
            <i className="fas fa-building-user"></i>
            <span>Boarding</span>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      <Modal
        isOpen={showSubscriptionModal}
        onRequestClose={() => setShowSubscriptionModal(false)}
        className="subscription-modal"
        overlayClassName="modal-overlay"
        ariaHideApp={false}
      >
        <div className="subscription-header">
          <h2 style={{fontSize: '20px'}}>Subscribe to Contact Landlords</h2>
          <button onClick={() => setShowSubscriptionModal(false)} className="close-button">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="subscription-content">
          <div className="subscription-plan">
            {subscriptionPlans.map((plan) => (
              <div key={plan.id} className="plan-card">
                <div className="plan-header">
                  <h3 style={{fontSize: '18px'}}>{plan.name}</h3>
                  <div style={{color: '#fff', backgroundColor: 'navy', padding: '10px', borderRadius: '5px'}} className="plan-price">
                    <span className="currency">$</span>
                    <span style={{fontSize: '20px'}} className="amount">{plan.price}</span>
                    <span className="duration">{plan.duration}</span>
                  </div>
                </div>
                <div className="plan-features">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <i className="fas fa-check"></i>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="payment-methods">
                  <h4 style={{fontSize: '18px'}}>Select Payment Method</h4>
                  <div className="payment-options">
                    <button
                      className={`payment-option ${paymentMethod === 'ecocash' ? 'selected' : ''}`}
                      onClick={() => handlePaymentMethodChange('ecocash')}
                      disabled={isProcessingPayment}
                    >
                      <img src="data:image/png;base64,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" alt="EcoCash" />
                      <span>EcoCash</span>
                    </button>
                    <button
                      className={`payment-option ${paymentMethod === 'visa' ? 'selected' : ''}`}
                      onClick={() => handlePaymentMethodChange('visa')}
                      disabled={isProcessingPayment}
                    >
                      <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAL8AAACUCAMAAAD1XwjBAAAA3lBMVEXrABv3nhv/////XwAAAADqAAD3mQD3oBz7+/v/XAD4+Pg/Pz8sLCzrABb/YgBlZWXQ0NDj4+MnJye0tLTc3NyLi4t6enr3lQDw8PD4lRjIyMjq6upeXl6dnZ32ox3+ZgYdHR2+vr43NzdPT0/+9ez97NxISEhvb28RERH4pTT97/D5smD7zJv6Twv9cg3yLxT95c75uW/74ODvZWfuUVL5zM/4rFPxfoHsNzjxcnf6woX71Kr4wML83Lv2QBD6jBX0nJ72sbPsIC/tO0X4qkXzkJLvW178xLn7fRD2pqujIA8uAAAK/klEQVR4nO2cZ3vquBKAZWMcx0AoLrBg30NvIffQa3KWk7J38///0Jfkihs2GWDZJ/MJXKR3RiNpLMuD2GQynE4Xvz9Ws/n6FaHXt/lstX9eTIfDhMWwrNDudF5G28nyfYDlffm4Hb2MO+12wmJQkiox+eY1TSWXQwjlcjnj39vs4/dCiF9SZzeavPPlcpnneYYK/oH/84PJaDc+D/9iM18TcBQgRI3X+WoRq6DxdjngbXCP4OOD5Ta+CvH4h4uNZfNQoW2xWkR7Uns84svB5G4lyvykE8+T4vBP93+F2N0n6fT8cxpOv3ssl4/BWyosRx0Q/uEKu00seFOD9Sa4DYTtIMRpQjxpsD3eBsf4p5u4pnccKZ3e+Nugsz3uN/5GmBxrg2j+6QdKYHpXI+Q+DjVoj+5iOo5HA2YbrUEk/+c6qe2dNlh/ugp6GSS2va3BYHci//TtVHpDgzdrOO08nmR7S8qDiCYI5Rf2uZNcx5E02tMpbXe68c0mYEahc2MYvzj/ivGtJpgN2fbki/RUg2XYSBTC//xV45tNkP7fV41vKsC/JOH/QF82viG/nv5m7iAUYJhRfP7V133HkKeH1P1/gRTgtzH5hzMQ38H+f/+QSqXuU3/AKFB+DOgEfn7xLyj8J4KPFfgBpAD/6B+GfPwilPXRvYGPFXiAciG/Al7+IZj1bXxAF+J9LuTlB7P+k4NPXAisBaL5V2fBB1SgvI3i/4DC/3WInyLDKIzwo3D+Z6hp66cXHyvw93+ANHgJ4x9CTVs/ffRE/gSaiPl2MD8O2WDw0b3f/JCj6FII5N+fzfmtLgDkQe4u4PBPo5dH4kuw9xAFwDyoE8C/Bg4bAvjBZoGBn//zzN5jjEFADVDeefmnayDvQaH0RAEeSAH7kdjiB5u5Qr3H8CCoHrA95J9CzVy5KPMDdmGmc8C/uYj5IcOIiZt/CIX/8z7a/nANUG67+MHCzojBx2oA2B6AYAefY/jgQxC6RORw2ACgUQThB3tmRMe8nwrYo1jb5J+C9d44+HCTcMfkv9TgaTkQVA+emPxQ+LmnOPZPQS0HMWWDfwHmPnHMDzkFjCn/udYcwh0IBp86EGKFOdTgH2v0IQoA8TNLwr+AmrxyP+LyQ3WAwRjz/76w+wMupfA7zH+ZyP+A/wfcFIzAVvtju38q9QA2A7TREMz9Y+OnoJajmUEbXTZ4MAVsBuggsNkrRuxpCVwINEZgw8+v+OaH439B51syj+AHC+FG6LLBp8UPg48fIhHYy9IE/GAhKP+IoKKf6/AvEdjwfw1+ZoBeYfBRLv70+81vyx0Q/fX4b9v+g5vvv7c9fr7f/Px12/HDBF1y7dbhh4vfbj1+hlt9uwb/+OafH2/9+f066ydgrzAQ3MujK6xfbf8F64eL14uv30J1X7p+e+vr5xd8eW3iw60+UP7nm31/9GK+vwMLoWPxQ7+/u/j7Uxh65/3pZUMgwODH2j8At/EzjgJ/wOAb3/T8G/ZvwO2fycXgB4vdnP0zcD04xv4lGPqD/UuA+8eOmv8s+8fO9NlFoPlhP8Sw9k9CbX6+1P5JaxO9yS9cZhMH9O69M+wfjhyCgAcfF/+N7d+2vwBw9s+/XWD/PNSyYcD+eRwFne/jHeDOWx4H8N/69yOAnw4GetD9Awz94cfY5/l+KrABgGdePz+7APuE5yrfr7HCOb8fBFsyZ0P5z/coCffp0YSN4mfBtnJ7vp8F2zDARvP/w79f9uWB8H0/DvYw7ChwDxX2BKSxCMg/AK3A/QPY21I/7FnzJ9A+AJh+IFb+BJaMQoD5K8C+HJ8EoZ49fwjUAyMTmH4jNH/LaXmXvJJGUPlbmET5W3AneIXInzOfAuXPGSTMn0PyF321CdLog37oPbpG/iIsiy/mj5pb+aPGX8wf9R6Rji8qf9dwf7IT5dK5vaukHXN6/i5mFJUF7kj+tM2J+dPQyps/7TQn4plJdA67Y/nrFqvE+etQOr3xZ3LsbJMk3zPpy0eyv8XJHzidoST5A3NpNAvOgNjeMsnyBzJHs+/FzN/4ETd/Yy6dnu8j8jeOHuNqwPPLo7aPy0/zZ+aO5c+kSUBXz0fzZzJx8mcy2zFc/kwqwmL2iqLyl6L15gi8WdB4O2DC0pfSE4PJOHYu10T5Y6efq9nayh9riPFvPfvYx0u+akhnt6X5Y3l//thtrLSfJ/ETGU4Xz/vVZr4mNs+h1/ls9UkS+CYsBntSZ/yyG02W73d3xOTvy8l2Nx7HzLrqSFJ+UwRbTrvfW9TJd5/I/4+Rb/7ryjf/deWb/+JSaTSq9p8b5C9wXMb+A8pfUVRVhCwwUKpn46/VOa4CWWCgnM/+tew3/3EJ4RdkuSiytYbUKBg+LOOfsvtGcqAgsKJcLJpHKgVJkqoyDb8EUSxi/ppIxDwvFtxlVHANuHpJMitgxWKx6OovlSouDOtvHZVlfGsRl1B01R/KX2w1tYrU7XNcScNX1dQMx3HNvIXKyvkMPpfNy8VmpmUc0lslfE0/k9dxfQ1Na+ELNCKKEVBKWpaU0TM1aGSbaqWHb8nQRqr2tG63q6kFswKlhbtPvaULvUyLHBOamRKrEwqJ8uXJz2xPDuMvcZkeZ4pUtH5xZuWSfUDBGtJDLfsQpwquCzguT+wn2qUZAOSKVpf8bR2e7ZGTtb59c4srUX6jLizUVu76A/mzFETXNWKzPpdVdYVo3KJt2aAVKbpKqylZJfZ0XccN1cRKVvOahk+qRHRif1J3y7yjapugq+g98i+Pf9d7ag/bXCX4GeNqWqXDz5VUXVWte3FtKlUhlL8hklGc1lPEDEVirgY5i6vJkpNioWvyi7go4jesgF2U/K9UZMv/Cb5MTFXDpwtZs0EIg4b9kZymyssVsSIrtMuT/3qNKKK6+UtVAXcs/LtODuILxWomgt9oZ5a0QM1welKsWbzpSMWmwV/RuL4zj1M5GH800zFwh6sbDYD5S1Z36htmt+806zFvtPmtY7pNxMrNUP5swb46a/zCkBzpjA4M9Qtqf+zA+YZ7/DjgF0vOb9UAkZyKiTu678SnmtbVsovfOkYwXPWH8GeKdml5k0Kl/GLGdGGrfMsm/a6WV6UgfrnO1XVTWoaxcbEaa1vIbX7ClLd+V1z91yoYl2ANU6HjZwR/rWk3Hy3XGH9Uz/hywF+1xxMqmuAultxqa00k77Ivblgvv5yx3RfrdwI/vr/out/gZxtK90ABNz9x+n4Uv+XaFr/dHsf4a8n52a7LXgWbnxVqstwgI6Emevll0mllW4oHxQb5T8u2T9PHj13K8d+Q+DOSP++6R3P4DWn0TeyD8afpMfEBv8wdRkoFs8/S4jgfP9tz6aedwC87Tk5oKb9sWUQucXUKIzZdzewactmql59oZzUAuRXb3JgjjHHcx1916pfqJ/CTWKGvkPlDqdvzV90IXQr4XM+oG1fdldmaTgyPuwme8ugFvazs5ScDqErIRaVpadsiV0kZLoCf6Nsns6VRf3J+Or8bkjX43fFOyRMklYg6Bdp9u/RGTfTwG/FMiUR8xqCq2IX16wH8lay7/kD+Otd0+DUPPw4/rZEEm65PDiglq8SWPTZr5hHZahjzgoZ4UCyhk+zRSaFRiK2A1OLqPn6n/jyZ6wL4K4qim12qgCMwk19SrelJrOLQt69JYrGnGmN1sarkcQCsF5yeiANwbB3FDPArVRXr2FSrFU+xxu2SVifxXMFcvZV1EhnosqD0VGpIVXGPUWJBzXJ1rSHKvZ49V7D/B9wgeW9633AAAAAAAElFTkSuQmCC" alt="Visa" />
                      <span>Visa Card</span>
                    </button>
                  </div>
                </div>

                <button 
                  className="subscribe-btn"
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isProcessingPayment || !paymentMethod}
                >
                  {isProcessingPayment ? (
                    <span className="processing">
                      <i className="fas fa-spinner fa-spin"></i>
                      Processing...
                    </span>
                  ) : (
                    'Subscribe Now'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AllProperties;
